import { ArrowRight } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { cateringPages } from '../../data/seo-catering';
import { cateringExtraPages } from '../../data/seo-catering-extra';
import { transitionPages } from '../../data/seo-transition';
import { airportPages } from '../../data/seo-airports';
import { airportExtraPages } from '../../data/seo-airports-extra';
import { airportPages2 } from '../../data/seo-airports-2';
import { airportsBatch3 } from '../../data/seo-airports-batch3';
import { airportsBatch4 } from '../../data/seo-airports-batch4';
import { authorityPages } from '../../data/seo-authority';
import { aircraftPages } from '../../data/seo-aircraft';
import { trainingPages } from '../../data/seo-training';
import { hospitalityPages } from '../../data/seo-hospitality';

const CATERING_TRAINING_LINK = "/academy/catering-operations";
const ACADEMY_LINK = "/academy";
const READINESS_LINK = "/operational-assessment";
const CALENDLY_LINK = "https://calendly.com/nicolas-gate33/discovery-call";

const clusterMap = {
  'Catering Intelligence': 'catering',
  'Aircraft Catering': 'catering',
  'Catering by City': 'catering',
  'Career Transition': 'transition',
  'Airport Guide': 'airports',
  'Industry Authority': 'authority',
  'Hospitality Transition': 'hospitality',
};

const allPages = [
  ...cateringPages, ...cateringExtraPages, ...aircraftPages,
  ...transitionPages, ...trainingPages,
  ...airportPages, ...airportExtraPages, ...airportPages2, ...airportsBatch3, ...airportsBatch4,
  ...authorityPages, ...hospitalityPages,
];

const SEOPage = ({ data }) => {
  const relatedPages = useMemo(() => {
    const sameCluster = allPages.filter(
      p => p.cluster === data.cluster && p.slug !== data.slug
    );
    const shuffled = [...sameCluster].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  }, [data.cluster, data.slug]);

  useEffect(() => {
    document.title = data.metaTitle || data.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    if (metaDesc) metaDesc.setAttribute('content', data.metaDescription || '');

    // Schema.org Article structured data
    const existingScript = document.getElementById('seo-schema');
    if (existingScript) existingScript.remove();

    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": data.title,
      "description": data.metaDescription,
      "author": { "@type": "Organization", "name": "Gate 33", "url": "https://gate33.co" },
      "publisher": { "@type": "Organization", "name": "Gate 33", "url": "https://gate33.co" },
      "mainEntityOfPage": { "@type": "WebPage", "@id": `https://gate33.co/insights/${clusterMap[data.cluster] || 'catering'}/${data.slug}` },
    };
    const script = document.createElement('script');
    script.id = 'seo-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      const el = document.getElementById('seo-schema');
      if (el) el.remove();
    };
  }, [data]);

  return (
    <div data-testid="seo-page" className="min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 w-full">
          <div className="max-w-3xl">
            <span className="overline inline-block mb-4">{data.cluster}</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] mb-6">
              {data.title}
            </h1>
            <p className="text-lg text-[#5A5A5A] leading-relaxed max-w-2xl">
              {data.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-6">{data.introTitle}</h2>
            {data.introText.map((p, i) => (
              <p key={i} className="text-[#5A5A5A] leading-relaxed mb-4">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Section */}
      <section className="section-padding bg-[#FDFBF7]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="overline inline-block mb-4">From Real Operations</span>
              <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-6">{data.expertTitle}</h2>
              {data.expertText.map((p, i) => (
                <p key={i} className="text-[#5A5A5A] leading-relaxed mb-4">{p}</p>
              ))}
            </div>
            <div className="bg-white border border-[#E5E0D8]/60 p-8 rounded-xl">
              <h3 className="font-heading text-lg mb-4">{data.expertListTitle}</h3>
              <div className="border-l-2 border-[#C5A880] pl-6">
                <ul className="space-y-3 text-[#5A5A5A]">
                  {data.expertList.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#C5A880] mt-0.5">–</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Reality */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="overline inline-block mb-4">The Reality</span>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-6">{data.realityTitle}</h2>
            {data.realityText.map((p, i) => (
              <p key={i} className="text-[#5A5A5A] leading-relaxed mb-4">{p}</p>
            ))}
            <div className="w-16 h-px bg-[#C5A880] mx-auto mt-8" />
          </div>
        </div>
      </section>

      {/* Practical Section */}
      <section className="section-padding bg-[#FDFBF7]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="overline inline-block mb-4">Actionable Insights</span>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-8">{data.practicalTitle}</h2>
            <div className="space-y-4">
              {data.practicalItems.map((item, i) => (
                <div key={i} className="bg-white border border-[#E5E0D8]/60 p-6 rounded-xl">
                  <h3 className="font-heading text-base mb-2">{item.title}</h3>
                  <p className="text-[#5A5A5A] text-sm leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Insights */}
      {relatedPages.length > 0 && (
        <section data-testid="related-insights" className="section-padding bg-white">
          <div className="container-custom">
            <span className="overline inline-block mb-4">Related Insights</span>
            <h2 className="font-heading text-2xl sm:text-3xl tracking-tight mb-8">Continue Reading</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPages.map((page) => (
                <Link
                  key={page.slug}
                  to={`/insights/${clusterMap[page.cluster] || 'catering'}/${page.slug}`}
                  data-testid={`related-${page.slug}`}
                  className="group bg-[#FDFBF7] border border-[#E5E0D8]/60 p-6 rounded-xl hover:shadow-md transition-all duration-300"
                >
                  <span className="text-[9px] uppercase tracking-[0.2em] text-[#C5A880] font-semibold">{page.cluster}</span>
                  <h3 className="font-heading text-lg mt-2 mb-3 group-hover:text-[#C5A880] transition-colors">{page.title}</h3>
                  <p className="text-[#5A5A5A] text-sm line-clamp-2">{page.subtitle}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#C5A880] mt-4 uppercase tracking-wider">
                    Read more <ArrowRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-padding bg-[#0F0F0F]">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            {data.ctaTitle}
          </h2>
          <p className="text-[#A0A0A0] leading-relaxed max-w-xl mx-auto mb-10">
            {data.ctaText}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {data.ctaPrimary === 'catering' && (
              <Link
                to={CATERING_TRAINING_LINK}
                data-testid="seo-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
              >
                Master the Catering Layer
                <ArrowRight size={16} />
              </Link>
            )}
            {data.ctaPrimary === 'readiness' && (
              <>
                <Link
                  to={READINESS_LINK}
                  data-testid="seo-cta-primary"
                  className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
                >
                  Take the Free Readiness Assessment
                  <ArrowRight size={16} />
                </Link>
                <a
                  href={CALENDLY_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="seo-cta-secondary-readiness"
                  className="inline-flex items-center justify-center gap-2 border border-[#C5A880] text-[#FDFBF7] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
                >
                  Or book a free Discovery Call
                </a>
              </>
            )}
            {data.ctaPrimary !== 'catering' && data.ctaPrimary !== 'readiness' && (
              <a
                href={CALENDLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="seo-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
              >
                Book a Free Discovery Call
                <ArrowRight size={16} />
              </a>
            )}
            {data.ctaPrimary === 'readiness' ? (
              <Link
                to={ACADEMY_LINK}
                data-testid="seo-cta-tertiary"
                className="inline-flex items-center justify-center gap-2 border border-[#5A5A5A] text-[#FDFBF7] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:border-[#C5A880] hover:text-[#C5A880] transition-all duration-300"
              >
                See the 3-Day Training
              </Link>
            ) : (
              <Link
                to="/"
                data-testid="seo-cta-secondary"
                className="inline-flex items-center justify-center gap-2 border border-[#5A5A5A] text-[#FDFBF7] px-8 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:border-[#C5A880] hover:text-[#C5A880] transition-all duration-300"
              >
                Explore Gate 33
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SEOPage;
